import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, Building2, CalendarDays, Eye, Loader2, Mail, Plus, Send, X } from "lucide-react";
import {
  addReportRecipient,
  fetchReportRecipients,
  previewReport,
  removeReportRecipient,
  sendTestReport,
  type ReportRecipient,
  type ReportRecipientsResponse,
  type ReportType,
} from "../../services/reportRecipients";

/**
 * Settings → Compliance Reports. Admins and directors can see who receives
 * these and preview / test them; only the named recipient managers
 * (lib/recipientManagers.ts) can add or remove recipients:
 *   - Weekly Enterprise Report   (Mondays, previous Mon–Sun, every facility)
 *   - Daily Combined Report      (every morning, all facilities in one email)
 *   - Daily Facility Reports     (every morning, one per facility, to its leaders)
 * Each report can be previewed in the browser or sent as a test to yourself.
 */

const fmtWhen = (iso?: string) =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;

function RecipientChips({ list, onRemove, busy }: { list: ReportRecipient[]; onRemove?: (r: ReportRecipient) => void; busy: string | null }) {
  if (!list.length) return <span className="text-xs italic text-gray-400">No recipients yet — this report is not sent.</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((r) => (
        <span key={r.id} className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700 dark:border-[#33334d] dark:bg-[#1c1c30] dark:text-gray-200">
          {r.name ? <><span className="font-medium">{r.name}</span><span className="text-gray-400">· {r.email}</span></> : r.email}
          {onRemove && (
            <button
              onClick={() => onRemove(r)}
              disabled={busy === r.id}
              className="ml-0.5 rounded text-gray-400 hover:text-red-600 disabled:opacity-40"
              aria-label={`Remove ${r.email}`}
              title="Remove"
            >
              {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

function AddRecipient({ onAdd, busy }: { onAdd: (email: string, name: string) => Promise<void>; busy: boolean }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    await onAdd(email.trim(), name.trim());
    setEmail("");
    setName("");
  };
  return (
    <form onSubmit={submit} className="mt-2 flex flex-wrap items-center gap-2">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="name@emory.edu"
        className="min-w-[200px] flex-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm outline-none focus:border-blue-500 dark:border-[#33334d] dark:bg-[#1c1c30] dark:text-gray-100"
      />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name (optional)"
        className="w-40 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm outline-none focus:border-blue-500 dark:border-[#33334d] dark:bg-[#1c1c30] dark:text-gray-100"
      />
      <button
        type="submit"
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
      </button>
    </form>
  );
}

function Actions({ onPreview, onTest, busy }: { onPreview: () => void; onTest: () => void; busy: string | null }) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      <button onClick={onPreview} disabled={!!busy} className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-[#33334d] dark:text-gray-200 dark:hover:bg-[#2c2c48]">
        {busy === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Preview
      </button>
      <button onClick={onTest} disabled={!!busy} className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-[#33334d] dark:text-gray-200 dark:hover:bg-[#2c2c48]">
        {busy === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test to me
      </button>
    </div>
  );
}

export const ComplianceReportsSettings: React.FC = () => {
  const [data, setData] = useState<ReportRecipientsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // recipient id | `${type}|${hospital}|add|preview|test`
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [openFacility, setOpenFacility] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchReportRecipients());
    } catch (e: any) {
      setError(e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const of = useMemo(() => {
    const list = data?.recipients ?? [];
    return (type: ReportType, hospitalId: string | null = null) =>
      list.filter((r) => r.report_type === type && (type !== "daily_facility" || r.hospital_id === hospitalId));
  }, [data]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try { await fn(); } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };
  const add = (type: ReportType, hospitalId: string | null) => (email: string, name: string) =>
    run(`${type}|${hospitalId}|add`, async () => {
      await addReportRecipient({ report_type: type, hospital_id: hospitalId, email, name: name || undefined });
      await load();
    });
  const remove = (r: ReportRecipient) => run(r.id, async () => { await removeReportRecipient(r.id); await load(); });
  const doPreview = (type: ReportType, hospitalId: string | null) =>
    run(`${type}|${hospitalId}|preview`, async () => {
      const p = await previewReport(type, hospitalId);
      // The logo travels as an email attachment; show the hosted copy in the browser.
      setPreview({ subject: p.subject, html: p.html.replace(/cid:guardai-gold-icon/g, "/g-data-gold-icon.png") });
    });
  const doTest = (type: ReportType, hospitalId: string | null) =>
    run(`${type}|${hospitalId}|test`, async () => {
      const r = await sendTestReport(type, hospitalId);
      setNotice(`Test email sent to ${r.to}.`);
    });
  const busyFor = (type: ReportType, hospitalId: string | null) =>
    busy?.startsWith(`${type}|${hospitalId}|`) ? busy.split("|")[2] : null;

  if (!data) {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
        {error ? <p className="text-sm text-red-700">{error}</p> : <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
      </section>
    );
  }

  const canManage = !!data.can_manage;
  const removeFn = canManage ? remove : undefined;

  const enterpriseBlock = (type: ReportType, title: string, desc: string, Icon: any) => (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-[#33334d]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <Icon className="mt-0.5 h-[18px] w-[18px] text-indigo-500" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
            {data.last_sent[type] && <p className="mt-0.5 text-[11px] text-gray-400">Last sent {fmtWhen(data.last_sent[type]!.at)}</p>}
          </div>
        </div>
        <Actions onPreview={() => doPreview(type, null)} onTest={() => doTest(type, null)} busy={busyFor(type, null)} />
      </div>
      <div className="mt-3">
        <RecipientChips list={of(type)} onRemove={removeFn} busy={busy} />
        {canManage && <AddRecipient onAdd={add(type, null)} busy={busyFor(type, null) === "add"} />}
      </div>
    </div>
  );

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-5 w-5 text-blue-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Automated Compliance Reports</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Daily reports: {data.schedule.daily.toLowerCase()}. Weekly report: {data.schedule.weekly.toLowerCase()}.
            Only the people listed here receive them.
          </p>
          {!canManage && (
            <p className="mt-1 text-xs text-gray-400">
              Recipients are managed by G-Data Labs and designated Emory leadership. Contact them to add or remove someone.
            </p>
          )}
        </div>
      </div>

      {!data.email_configured && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Email delivery isn't configured on the server yet (RESEND_API_KEY), so reports can be previewed but not sent.
        </div>
      )}
      {error && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}
      {notice && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="mt-5 space-y-4">
        {(canManage || data.can_view_enterprise) && (
          <>
            {enterpriseBlock("weekly_enterprise", "Weekly Enterprise Report",
              "Enterprise 2-hour compliance rate for the previous week, with each facility's rate, change from the prior week and lowest day.", BarChart3)}
            {enterpriseBlock("daily_combined", "Daily Report — All Facilities",
              "One email each morning listing every facility's 2-hour compliance rate for the previous day.", CalendarDays)}
          </>
        )}

        <div className="rounded-lg border border-gray-200 dark:border-[#33334d]">
          <div className="flex items-start gap-2.5 border-b border-gray-200 p-4 dark:border-[#33334d]">
            <Building2 className="mt-0.5 h-[18px] w-[18px] text-indigo-500" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Daily Facility Reports</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Each facility's leadership receives the previous day's 2-hour compliance rate, rates by shift and by floor, and the least-covered checkpoints.
              </p>
              {data.last_sent.daily_facility && <p className="mt-0.5 text-[11px] text-gray-400">Last sent {fmtWhen(data.last_sent.daily_facility.at)}</p>}
            </div>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-[#33334d]">
            {data.facilities.map((f) => {
              const list = of("daily_facility", f.id);
              const open = openFacility === f.id;
              return (
                <li key={f.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button onClick={() => setOpenFacility(open ? null : f.id)} className="text-left">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{f.name}</span>
                      <span className="ml-2 text-xs text-gray-400">{f.checkpoints} checkpoints · {list.length} recipient{list.length === 1 ? "" : "s"}</span>
                    </button>
                    <div className="flex items-center gap-2">
                      <Actions onPreview={() => doPreview("daily_facility", f.id)} onTest={() => doTest("daily_facility", f.id)} busy={busyFor("daily_facility", f.id)} />
                      {canManage && (
                        <button onClick={() => setOpenFacility(open ? null : f.id)} className="rounded-md px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-[#2c2c48]">
                          {open ? "Done" : "Edit"}
                        </button>
                      )}
                    </div>
                  </div>
                  {(open || list.length > 0) && (
                    <div className="mt-2">
                      <RecipientChips list={list} onRemove={removeFn} busy={busy} />
                      {open && canManage && <AddRecipient onAdd={add("daily_facility", f.id)} busy={busyFor("daily_facility", f.id) === "add"} />}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreview(null)}>
          <div className="flex h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-gray-400">Preview · subject</p>
                <p className="truncate text-sm font-medium text-gray-900">{preview.subject}</p>
              </div>
              <button onClick={() => setPreview(null)} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Close preview"><X className="h-5 w-5" /></button>
            </div>
            <iframe title="Report preview" srcDoc={preview.html} sandbox="" className="h-full w-full flex-1 bg-gray-100" />
          </div>
        </div>
      )}
    </section>
  );
};
